import React, { useState } from "react";
import {
  AccordionContent,
  AccordionHeader,
  AccordionIcon,
  StyledAccordion,
} from "./Accordion.styled";
import { ReactComponent as ChevronDownIcon } from "../../../icons/chevronDown.svg";
import { IconManager } from "../IconManager/IconManager";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { BodyTyp } from "../../Typography/Typography";
import { theme } from "../../../constants/StyledComponents";

export enum AccordionVariantEnum {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

interface AccordionProps {
  children: React.ReactNode;
  label?: string | React.ReactNode;
  defaultOpen?: boolean;
  collapsible?: boolean;
  leftComponent?: React.ReactNode;
  rightComponent?: React.ReactNode;
  variant?: AccordionVariantEnum;
  selectedCount?: number;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  toScroll?: boolean;
}

export const Accordion = ({
  children,
  label,
  defaultOpen = false,
  collapsible = true,
  rightComponent,
  leftComponent,
  selectedCount,
  variant = AccordionVariantEnum.PRIMARY,
  isOpen: controlledIsOpen,
  onToggle,
  toScroll,
}: AccordionProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const isControlled = controlledIsOpen !== undefined;

  const toggleAccordion = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("[data-no-toggle]")) {
      return;
    }
    const newState = !isOpen;

    if (isControlled) {
      onToggle?.(newState);
    } else {
      setInternalIsOpen(newState);
      onToggle?.(newState);
    }
  };

  return (
    <StyledAccordion>
      <AccordionHeader
        $isCollapsible={collapsible}
        $variant={variant}
        onClick={collapsible ? toggleAccordion : undefined}
      >
        <div className="accordion-header-content">
          {leftComponent && <div data-no-toggle>{leftComponent}</div>}
          {label}
          {!isOpen && selectedCount !== undefined && selectedCount !== 0 && (
            <BodyTyp
              fontWeight={FontWeightEnum.MEDIUM}
              variant="2"
              color={theme.colors.purple}
            >
              {`+${selectedCount}`}
            </BodyTyp>
          )}
        </div>
        <div className="accordion-header-content">
          <div data-no-toggle>{rightComponent}</div>
          {collapsible && (
            <AccordionIcon $isOpen={isOpen}>
              <IconManager size={IconSizeEnum.SMALL}>
                <ChevronDownIcon />
              </IconManager>
            </AccordionIcon>
          )}
        </div>
      </AccordionHeader>
      <AccordionContent
        $variant={variant}
        $isOpen={isOpen}
        $toScroll={toScroll}
      >
        {isOpen && children}
      </AccordionContent>
    </StyledAccordion>
  );
};
